<template>
  <Cell
    :id="id"
    :class="{
      'cell-edit': editable,
      'cell-edit-currency': !noCurrency && !currencyAfter,
      'cell-number-currency': !noCurrency && !currencyAfter,
      'cell-with-unit': unitProps.used,
      'cell-currency-after': currencyAfter,
    }"
    :style="cssProps"
    :editable="editable"
    :contenteditable="editable"
    :error="err"
    :error-position="errorPosition"
    @click.native="click"
    @focus.native="select"
    @input.native="input"
    @blur.native="blur"
    @keydown.native.enter.prevent="enter"
    @keydown.native.esc.prevent.stop="goBack"
    @keydown.native="checkNumber"
    @contextmenu="(e) => $emit('contextmenu', e)"
  >
    <slot name="pre" />

    <div
      ref="cellContent"
      :class="['cell-content', classname]"
      :style="styleObject"
      @click="$emit('innerclick', $event)"
    >
      <slot>{{ spacedLabel }}</slot>
    </div>

    <slot name="after" />

    <div
      v-if="unitProps.used"
      class="unit"
      :class="[
        currencyClass,
        {
          right: unitProps.position === 'right',
          after: unitProps.position === 'after',
        },
      ]"
      contenteditable="false"
    >
      {{ unitProps.label || currencySymbols[currency] }}
    </div>

    <div v-if="unitProps.helper" class="value" contenteditable="false">
      <template v-if="unitProps.helperCurrency">
        {{ currencySymbols[currency] }}
      </template>
      {{ unitProps.helperText }}
    </div>
  </Cell>
</template>

<script>
  import Cell from '@/components/common/Cell';
  import addSpaces from '@/utils/formatting/add-spaces';
  import NAVIGATION_BUTTONS_KEY_CODES from '@/utils/constants/navigation-buttons';
  import { mapState } from 'vuex';

  const DEFAULT_UNIT = {
    used: false,
    label: null,
    position: 'right',
    helper: false,
    helperText: '',
    helperCurrency: false,
    helperPosition: 'right',
  };

  const ALLOWED_KEYS = [
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '0',
    '-',
    ',',
    '.',
    'Backspace',
    'Meta',
    'ArrowLeft',
    'ArrowRight',
    'ArrowDown',
    'ArrowUp',
  ];

  const BACKSPACE_BUTTON = 'Backspace';

  export default {
    name: 'NumberCell',
    components: { Cell },
    props: {
      id: {
        type: String,
      },
      label: {
        type: [Number, String],
        default: '',
      },

      /**
       * Hide currency
       */
      noCurrency: {
        type: Boolean,
        default: false,
      },

      currency: {
        type: String,
        default: null,
      },

      /**
       * This prop adds the currencyLabel as :after pseudo-element right after the
       * value of the cell, so it is not affected by contenteditable. Makes noCurrency=true
       */
      currencyAfter: {
        type: String,
        default: '',
      },

      editable: {
        type: Boolean,
        default: false,
      },

      classname: {
        type: String,
        default: '',
      },

      currencyClass: {
        type: String,
        default: '',
      },

      /**
       * Unit is an absolutely positioned text|number that can be hovered to see a popover
       * used: boolean – whether unit is used or not
       * label: string – what is the label of this unit. Overrides this.specialLabel || saved currency
       * {position}: 'right'|'after' – unit position. After means right after the number
       * helper: boolean – whether to show the popover on hover or not
       * helperText: string – what text to display in the popover
       * helperCurrency: boolean – show the saved currency before the helperText or not
       * helperPosition: 'right'|'left' – popover position
       */
      unit: {
        type: Object,
        default: () => ({}),
      },

      error: {
        type: String,
        default: '',
      },

      errorPosition: {
        type: String,
        default: 'right', // left|right
      },
    },

    data() {
      return {
        focus: false,
        val: this.label.toString(),
        oldVal: null,
        err: this.error,
        styleObject: {
          '--afterContent': this.currencyAfter ? `"${this.currencyAfter}"` : '',
        },
      };
    },

    computed: {
      ...mapState('global', ['currencySymbols']),
      // Merge default unit props with what is passed
      unitProps() {
        return { ...DEFAULT_UNIT, ...this.unit };
      },

      spacedLabel() {
        if (this.val || this.val === 0) return addSpaces(this.val);
        return this.val || ' ';
      },

      cssProps() {
        return {
          '--currency-symbol': `'${this.currencySymbols[this.currency]}'`,
        };
      },
    },

    watch: {
      error(val) {
        this.err = val;
      },

      label(val) {
        this.val = val.toString();
      },
    },

    methods: {
      click(e) {
        if (!this.editable) return;
        this.$emit('click', e);
      },

      select(e) {
        if (!this.editable) return;

        this.focus = true;
        this.oldVal = this.val.trim();

        let range = document.createRange();
        range.selectNodeContents(e.target.firstChild);
        let selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      },
      input() {
        this.err = '';
      },
      blur(e) {
        this.focus = false;
        const val = e.target.firstChild.textContent?.trim()?.replaceAll(' ', '')?.replace(',', '.');
        if (e.target.firstChild && val !== this.val) {
          this.val = val;
          this.$emit('change', {
            event: e,
            value: val,
            oldVal: this.oldVal,
          });
        }
      },
      enter(e) {
        this.focus = false;
        e.target.blur();
      },
      goBack(e) {
        if (this.oldVal) {
          e.target.firstChild.textContent = this.oldVal;
          this.val = this.oldVal;
        }
        this.oldVal = null;
        e.target.blur();
      },

      checkNumber(e) {
        if (NAVIGATION_BUTTONS_KEY_CODES.includes(e.keyCode)) {
          this.$emit('change-focused-cell', { e, value: this.val, id: this.id });
        }
        const contentLength = this.$refs.cellContent.textContent.length;
        const highlightedField = document.getSelection().toString().length === contentLength;

        // manual clearing of fields to control tag content
        if (e.code === BACKSPACE_BUTTON && (contentLength <= 1 || highlightedField)) {
          this.$refs.cellContent.textContent = '';
          this.abortAction(e);
        }

        if (e.metaKey || ALLOWED_KEYS.includes(e.key)) return;

        this.abortAction(e);
      },
      abortAction(e) {
        e.preventDefault();
        e.stopPropagation();
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import 'src/styles/colors';

  .cell-content:after {
    content: var(--afterContent);
    color: #b0b2c0;
  }

  .cell-edit-currency .cell-content:before {
    content: var(--currency-symbol);
    color: $text-secondary;
    margin: 4px;
  }
</style>
